/**
 * Hardcoded stand-ins for fields the design shows but the GraphQL API does not
 * expose yet. Every export here maps to a ticket in
 * docs/design/backend-requirements.md. When a field lands in the API, delete the
 * stub, read the real value in the route loader, and keep passing the same prop.
 *
 * Values are derived deterministically from the place slug so a given place
 * always looks the same between renders. These feed display only -- the map
 * filters run on real API data (cities, districts, labels).
 */

export type PriceLevel = 'budget' | 'moderate' | 'premium';

export type PlaceExtras = {
  // BACKEND: CF-1
  rating: { score: number; verdict: string };
  // BACKEND: CF-2
  openingHours: { summary: string; note: string; openNow: boolean };
  // BACKEND: CF-3
  phone: string;
  website: string;
  // BACKEND: CF-4
  priceLevel: PriceLevel;
};

export const PRICE_SYMBOL: Record<PriceLevel, string> = {
  budget: '€',
  moderate: '€€',
  premium: '€€€',
};

const VERDICTS = [
  'Krokant vanbuiten, vol van smaak vanbinnen. Geen hype, gewoon een vak dat al jaren wordt verstaan.',
  'Familiebedrijf zoals het hoort. Alles vers, porties royaal, en de vaste gasten weten precies waarom.',
  'Klein zaakje, grote smaken. Kom vroeg, want als het op is, is het op.',
  'Een buurtinstituut. De kaart is kort, de uitvoering scherp, en de sfeer maakt het af.',
];

const PRICE_ROTATION: PriceLevel[] = [
  'budget',
  'moderate',
  'budget',
  'premium',
];

function hash(value: string): number {
  let acc = 0;
  for (let i = 0; i < value.length; i += 1) {
    acc = (acc * 31 + value.charCodeAt(i)) >>> 0;
  }
  return acc;
}

function pick<T>(pool: readonly T[], seed: number, offset = 0): T {
  return pool[(seed + offset) % pool.length] as T;
}

// BACKEND: CF-1, CF-2, CF-3, CF-4
export function placeExtras(slug: string, name: string): PlaceExtras {
  const seed = hash(slug);
  const score = 7.5 + (seed % 21) / 10; // 7.5 - 9.5
  const priceLevel = PRICE_ROTATION[seed % PRICE_ROTATION.length] as PriceLevel;
  const domain = `${name.toLowerCase().replace(/[^a-z0-9]+/g, '')}.nl`;

  return {
    rating: {
      score: Math.round(score * 10) / 10,
      verdict: pick(VERDICTS, seed),
    },
    openingHours: {
      summary: 'Di - Zo   11:00 - 20:00',
      note: 'Maandag gesloten',
      openNow: seed % 3 !== 0,
    },
    phone: `0${10 + (seed % 79)} - ${100 + (seed % 900)} ${1000 + (seed % 9000)}`,
    website: domain,
    priceLevel,
  };
}

// BACKEND: CF-7 -- embed views / age for list + grid cards.
export function embedMeta(id: string): { views: string; ago: string } {
  const seed = hash(id);
  return {
    views: `${18 + (seed % 120)}K`,
    ago: pick(
      ['2 dagen', '5 dagen', '1 week', '2 weken', '3 weken', '1 maand'],
      seed,
    ),
  };
}
